<!--
  CostBreakdownTable.vue
  콜봇 월 정산 · 정산 탭의 '원가 · 마진' 표 — 화면 명세 2-3 (항목별 원가 · 판매가 · 마진).

  Always visible (not folded): title '원가 · 마진' with 'vox 원가 · 마진' on the right, then the full table.
  Columns: 항목 | 사용량 | 원가 단가 | 원가 | 서티 단가 | 서티 금액 | 마진
  Every row is drawn — nothing is cut, truncated or hidden behind a toggle. Narrow widths scroll the table sideways.

  서티 청구 (item → child rows)
  - 월 사용료(`base`) — supply amount of the VAT-included fee (× 10 ÷ 11); opening month prorated by the included line days.
  - 120분 초과 통화(`inbound`) → 전화(수신) · 웹 음성 · 포함 통화 · 계산. Usage is counted in 10-second units by the server
    but always shown as time (settlementRules.unitsMinutesText) — never 'n단위'. Formula (overageFormulaText) example:
    '1,486분 20초 × 1분당 200원 = 297,266.66…원 → 원 미만 버림' (server amount; '…' when the exact product goes on).
    A human-entered amount turns '계산' into '계산값 근거'.
  - 발신(`outbound`) → 발신 건당 · 받은 발신 통화 ('68분 30초 × 10초당 50원'). Calls cancelled before dialing or failed
    to dial (0원) keep their count: '발신 79통 · 건 통화 78건' (settlementRules.outboundCountText).
  - 웹 텍스트(`web_text`) — vox message cost passed through or not billed; margin = sale − cost.
  - 회선(`line`) → every line: usage lines matched to `charge.lines.line.items`, the included 070 line too, plus any
    charge item without a usage line. Included line 0원 (월 사용료에 포함), unset sale price '미정' (LINE_RATE_UNSET).
  - 상담사 번호 추가(`counselor_number`) → 구간 · 계산 · 청구 창 · 매시 기록 없는 날 · 가장 많았던 날 · 안내
    (settlementRules.counselorNumberLineView). No cost — the whole amount is margin. Not on v70-legacy statements.
  - 일회성 항목 → every item (no cost, margin '—').
  원가 (vox cost that cannot be split per billed item)
  - AI 음성(`cost.split.voice_ai` — inbound, outbound and web voice, AI only rounded up per call to 10 s).
  - 상담사 연결(vox direct transfer + telecom; no sale price — included in the monthly fee) → 연결 구간 · every route × number
    type (answers / talk time) · vox 직접 전환 · vox 직접 통신료, gray reference PBX 트렁크 · STT estimates (excluded from
    totals). Nothing is hover-only.
  - 기타 원가(`cost.split.other` — cold transfers, outbound base fee, extras; not billed).
  Web text and line costs sit on their billed items. All cost rows add up to `cost.total`.
  Margin: base / call items share one AI cost, so their margin is read on the total only ('합계에서'). Row margins exist for
  web text, non-included lines and the counselor-number item.
  Footer: 합계(공급가액) with vox 원가 · 공급가액 · 마진 and the formulas, 부가세 10%, 합계(부가세 포함), then the gray
  reference margin after the PBX trunk (and STT) estimates.
  While the review amounts are being typed (`live`), the totals and the margin follow the typed amounts.
  Badges: rate source next to every sale unit (기본 / 예외), '직접 입력' (human amount — '직접 입력 · 계산값 n원'),
  '웹 사용량 미확인', warning codes, '미정 제외'.
  No default rate: sale columns '—' (manual-only months show the human amounts only); internal/test: sale 0원, margin '—';
  v70-legacy statements: one-off items and cost rows only, with what to do first by status.

  props: detail · month · summary(settlementRules.summaryLine) · live({ supply, margin } of the review input, or null)
  Data: the settlement detail passed by the parent. This table calls nothing.
  Rows · cells · totals: service/costBreakdownModel.js (pure — the Excel export builds the same rows). Cells are { text, value }.
  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-3", "요청 계약 2"(usage·cost·charge·margin)
-->
<template>
  <section class="cost-more" :aria-labelledby="`${uid}-title`">
    <header class="cost-more__head">
      <h2 :id="`${uid}-title`" class="cost-more__title">원가 · 마진</h2>
      <span class="cost-more__sum">
        vox 원가 {{ formatWon(summary.cost.total) }} · 마진
        <span :class="{ 'is-negative': isNegative(margin) }">{{ formatWon(margin) }}</span>
        <span v-if="live" class="cost-more__pill is-override">입력 중</span>
      </span>
    </header>

    <div class="cost-more__body">
      <p class="cost-more__legend">
        <span v-if="sourceLabel" class="cost-more__pill" :class="`is-${sourceTone}`">{{ sourceLabel }}</span>
        <span v-if="legacy">공급가액 · 부가세 별도 · 회색 ‘참고’ 원가는 합계에 넣지 않습니다</span>
        <span v-else>
          공급가액 · 부가세 별도 · 월 사용료·통화 항목은 원가를 항목마다 나눌 수 없어 마진을 합계에서 봅니다 · 회색 ‘참고’
          원가는 합계에 넣지 않습니다
        </span>
      </p>

      <p v-if="legacy" class="cost-more__notice is-warn" role="status">{{ legacyText }}</p>
      <p v-else-if="!priced && !notBillable" class="cost-more__notice is-warn" role="status">
        <span>{{ manualOnly ? MANUAL_ONLY_TEXT : planMissingText }}</span>
        <router-link :to="ratesLocation" class="cost-more__link">{{ planMissingLinkText }}</router-link>
      </p>
      <p v-else-if="internal" class="cost-more__notice" role="status">
        내부·테스트 회사는 청구가 0원입니다. 서티 단가는 참고로만 보이고, 서티 금액은 0원, 마진은 계산하지 않습니다.
      </p>
      <p v-if="drift" class="cost-more__notice is-muted" role="status">
        확정 이후 사용량이 바뀌었습니다. 이 표의 사용량·금액·원가는 모두 확정 시점 값입니다. 바뀐 사용량을 반영하려면
        재수정합니다.
      </p>
      <p v-if="live" class="cost-more__notice is-muted" role="status">
        합계·마진은 정산서에 입력 중인 금액(저장 전)으로 계산했습니다. 항목별 서티 금액은 저장된 금액입니다.
      </p>

      <div class="cost-more__scroll" role="region" aria-label="원가·판매가·마진 표(가로로 스크롤)" tabindex="0">
        <table class="cost-more__table">
          <caption class="cost-more__sr">원가 · 판매가 · 마진 (항목별)</caption>
          <colgroup>
            <col class="cost-more__col-name" />
            <col class="cost-more__col-usage" />
            <col class="cost-more__col-unit" />
            <col class="cost-more__col-amount" />
            <col class="cost-more__col-unit" />
            <col class="cost-more__col-amount" />
            <col class="cost-more__col-amount" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">항목</th>
              <th scope="col">사용량</th>
              <th scope="col">원가 단가</th>
              <th scope="col" class="is-r">원가</th>
              <th scope="col">서티 단가</th>
              <th scope="col" class="is-r">서티 금액</th>
              <th scope="col" class="is-r">마진</th>
            </tr>
          </thead>

          <tbody>
            <template v-for="row in rows" :key="row.key">
              <!-- Section head (서티 청구 / 원가) -->
              <tr v-if="row.kind === 'section'" class="cost-more__section">
                <th scope="colgroup" colspan="7">
                  <span class="cost-more__section-title">{{ row.name }}</span>
                  <span v-if="row.sub" class="cost-more__section-sub">{{ row.sub }}</span>
                </th>
              </tr>
              <!-- Basis row — one text across the value columns (formula, segments, notes) -->
              <tr v-else-if="row.kind === 'detail'" class="cost-more__row is-level-1 is-detail">
                <th scope="row" class="cost-more__name">
                  <span class="cost-more__name-line">
                    <span class="cost-more__name-main">{{ row.name }}</span>
                  </span>
                  <span v-if="row.sub" class="cost-more__name-sub">{{ row.sub }}</span>
                </th>
                <td colspan="6" class="cost-more__detail">{{ row.detail.text }}</td>
              </tr>
              <tr v-else class="cost-more__row" :class="[`is-level-${row.level}`, { 'is-group': row.level === 0 }]">
                <th scope="row" class="cost-more__name">
                  <span class="cost-more__name-line">
                    <span class="cost-more__name-main">{{ row.name }}</span>
                    <a-tooltip v-if="row.warning" :title="warningTooltip(row.warning)">
                      <span class="cost-more__code" :class="{ 'is-blocking': blockingCodes.has(row.warning) }">
                        {{ row.warning }}
                      </span>
                    </a-tooltip>
                    <a-tooltip v-if="row.partial" :title="row.partialTip">
                      <span class="cost-more__pill is-warn">미정 제외</span>
                    </a-tooltip>
                    <a-tooltip v-if="row.override" :title="row.override.memo || undefined">
                      <span class="cost-more__pill is-override" :class="{ 'has-tip': !!row.override.memo }">
                        직접 입력
                      </span>
                    </a-tooltip>
                    <a-tooltip v-if="row.webNotice" :title="row.webNotice.tooltip">
                      <span class="cost-more__pill is-warn">{{ row.webNotice.label }}</span>
                    </a-tooltip>
                  </span>
                  <span v-if="row.sub" class="cost-more__name-sub">{{ row.sub }}</span>
                </th>
                <td class="cost-more__usage">{{ row.usage.text }}</td>
                <td class="cost-more__unit" :class="{ 'is-muted': row.costUnit.muted }">{{ row.costUnit.text }}</td>
                <td class="cost-more__amount">
                  <span class="cost-more__value-line">
                    <span v-if="row.cost.ref" class="cost-more__ref">참고</span>
                    <span class="cost-more__value" :class="cellTone(row.cost)">{{ row.cost.text }}</span>
                  </span>
                  <span v-if="row.cost.note" class="cost-more__note">{{ row.cost.note }}</span>
                </td>
                <td class="cost-more__unit">
                  <span class="cost-more__value-line is-start">
                    <span :class="{ 'cost-more__missing': row.saleUnit.missing, 'is-muted': row.saleUnit.muted }">
                      {{ row.saleUnit.text }}
                    </span>
                    <span
                      v-if="row.saleUnit.source"
                      class="cost-more__src"
                      :class="{ 'is-override': row.saleUnit.source === 'override' }"
                    >
                      {{ rateSourceLabel(row.saleUnit.source) }}
                    </span>
                  </span>
                </td>
                <td class="cost-more__amount">
                  <span class="cost-more__value" :class="cellTone(row.sale)">{{ row.sale.text }}</span>
                  <span v-if="row.sale.note" class="cost-more__note">{{ row.sale.note }}</span>
                </td>
                <td class="cost-more__amount">
                  <span class="cost-more__value-line">
                    <span v-if="row.margin.ref" class="cost-more__ref">참고</span>
                    <span class="cost-more__value" :class="cellTone(row.margin)">{{ row.margin.text }}</span>
                  </span>
                  <span v-if="row.margin.note" class="cost-more__note">{{ row.margin.note }}</span>
                </td>
              </tr>
            </template>
          </tbody>

          <tfoot>
            <tr class="cost-more__total">
              <th scope="row" class="cost-more__name">
                <span class="cost-more__name-line">
                  <span class="cost-more__name-main">합계(공급가액)</span>
                  <a-tooltip v-if="summary.supply.partial && !live" :title="partialTotalTip">
                    <span class="cost-more__pill is-warn">일부 제외</span>
                  </a-tooltip>
                  <a-tooltip v-if="summary.supply.legacy" :title="LEGACY_STATEMENT_TEXT">
                    <span class="cost-more__pill is-warn">v70 이전</span>
                  </a-tooltip>
                  <span v-if="live" class="cost-more__pill is-override">입력 중</span>
                </span>
                <span class="cost-more__name-sub">부가세 별도 · 일회성 항목 포함</span>
              </th>
              <td colspan="2" class="cost-more__usage">
                <span v-for="line in totalNotes" :key="line" class="cost-more__total-note">{{ line }}</span>
              </td>
              <td class="cost-more__amount">
                <span class="cost-more__value">{{ formatWon(summary.cost.total) }}</span>
                <span class="cost-more__note">vox 원가</span>
              </td>
              <td></td>
              <td class="cost-more__amount">
                <span class="cost-more__value">{{ formatWon(supply) }}</span>
                <span v-if="summary.supply.legacy" class="cost-more__note">그때 저장한 정산서 금액</span>
                <span v-else-if="live" class="cost-more__note">입력 중인 금액</span>
              </td>
              <td class="cost-more__amount">
                <span class="cost-more__value" :class="{ 'is-negative': isNegative(margin) }">{{ formatWon(margin) }}</span>
                <span v-if="isNumber(margin)" class="cost-more__note">vox 원가 대비</span>
              </td>
            </tr>
            <tr class="cost-more__vat">
              <th scope="row" class="cost-more__name">
                <span class="cost-more__name-main">부가세 10%</span>
                <span class="cost-more__name-sub">공급가액의 10% · 원 미만 절사</span>
              </th>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="cost-more__amount">
                <span class="cost-more__value">{{ formatWon(vat) }}</span>
              </td>
              <td></td>
            </tr>
            <tr class="cost-more__vat">
              <th scope="row" class="cost-more__name">
                <span class="cost-more__name-main">합계(부가세 포함)</span>
              </th>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="cost-more__amount">
                <span class="cost-more__value is-strong">{{ formatWon(totalWithVat) }}</span>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p v-if="referenceMarginText" class="cost-more__reference">{{ referenceMarginText }}</p>
      <p v-if="!legacy" class="cost-more__foot">
        원 미만은 항목마다 한 번 버립니다(월 사용료는 VAT 포함 금액 × 10 ÷ 11, 120분 초과는 초과 시간 × 1분당 단가, 발신은
        건당과 받은 통화를 더한 뒤). 통화 시간은 통화마다 10초씩 올려 셉니다. 상담사 연결은 월 사용료에 들어 있어
        판매가가 없고 통화 시간만 120분 사용량에 듭니다. 서티 단가 옆 배지는 그 단가의 출처(기본 / 예외)입니다.
      </p>
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';

import { MANUAL_ONLY_TEXT, costBreakdownModel } from '../../service/costBreakdownModel';
import { formatWon, isNumber } from '../../service/settlementFormat';
import { LEGACY_STATEMENT_TEXT, SETTLEMENT_ROUTE_NAMES, rateSourceLabel, warningMeta } from '../../service/settlementRules';

const props = defineProps({
  /** 정산 상세 응답(요청 계약 2) */
  detail: { type: Object, required: true },
  /** 'YYYY-MM' — days in the month for the line proration */
  month: { type: String, required: true },
  /** settlementRules.summaryLine(detail) */
  summary: { type: Object, required: true },
  /** Review input in progress — { supply, margin } computed from typed amounts, or null */
  live: { type: Object, default: null },
});

const uid = `cost-more-${getCurrentInstance()?.uid ?? 0}`;

// Rows, cells and totals come from the pure model (service/costBreakdownModel.js) — the Excel export reuses it.
const model = computed(() =>
  costBreakdownModel(props.detail, { month: props.month, summary: props.summary, live: props.live })
);

const legacy = computed(() => model.value.flags.legacy);
const priced = computed(() => model.value.flags.priced);
const notBillable = computed(() => model.value.flags.notBillable);
const manualOnly = computed(() => model.value.flags.manualOnly);
const internal = computed(() => model.value.flags.internal);
const drift = computed(() => model.value.flags.drift);
const sourceLabel = computed(() => model.value.source.label);
const sourceTone = computed(() => model.value.source.tone);
const legacyText = computed(() => model.value.legacyText);
const planMissingText = computed(() => model.value.planMissingText);
const planMissingLinkText = computed(() => model.value.planMissingLinkText);
const rows = computed(() => model.value.rows);
const supply = computed(() => model.value.totals.supply.value);
const vat = computed(() => model.value.totals.vat.value);
const totalWithVat = computed(() => model.value.totals.totalWithVat.value);
const margin = computed(() => model.value.totals.margin.value);
const totalNotes = computed(() => model.value.totals.notes);
const partialTotalTip = computed(() => model.value.totals.partialTip);
const referenceMarginText = computed(() => model.value.referenceMarginText);

const ratesLocation = computed(() => ({ name: SETTLEMENT_ROUTE_NAMES.rates, query: { month: props.month } }));
const blockingCodes = computed(() => {
  const warnings = Array.isArray(props.detail.warnings) ? props.detail.warnings : [];
  return new Set(warnings.filter((w) => w?.blocking).map((w) => w.code));
});

const isNegative = (v) => isNumber(v) && v < 0;

function cellTone(cell) {
  return {
    'is-negative': cell.negative,
    'is-missing': cell.missing,
    'is-muted': cell.muted || cell.ref,
  };
}

function warningTooltip(code) {
  const meta = warningMeta(code);
  return `${meta.label} — ${meta.description}`;
}
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.cost-more {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  color: $st-text;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 4px 12px;
    padding: 16px 20px 0;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }

  &__sum {
    @include st-num;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    font-size: 14px;
    color: $st-text-secondary;

    .is-negative {
      color: $st-danger;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 20px 16px;
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    margin: 0;
    font-size: 12px;
    color: $st-muted;
  }

  &__pill {
    @include st-pill;

    &.is-accent {
      @include st-pill-tone($st-accent, $st-accent-bg, $st-accent-border);
    }

    &.is-warn {
      @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
      cursor: help;
    }

    // Human-entered amount — same color as '수정됨' on the statement
    &.is-override {
      @include st-pill-tone($st-override, $st-override-bg, $st-override-border);

      &.has-tip {
        cursor: help;
      }
    }
  }

  &__notice {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 12px;
    margin: 0;
    padding: 8px 12px;
    border: 1px solid $st-border;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-text-secondary;

    &.is-warn {
      border-color: $st-warning-border;
      background: $st-warning-bg;
      color: $st-warning;
    }

    &.is-muted {
      color: $st-muted;
    }
  }

  &__link {
    font-weight: 600;
  }

  // Narrow widths scroll the table only (never the page). position: relative keeps the hidden caption inside
  &__scroll {
    position: relative;
    overflow-x: auto;
    border: 1px solid $st-border;
    border-radius: 10px;

    &:focus-visible {
      outline: 2px solid $st-accent-border;
      outline-offset: 2px;
    }
  }

  &__table {
    width: 100%;
    min-width: 980px;
    border-collapse: collapse;
    font-size: 12.5px;

    // keep-all: Korean words never split mid-word ('통신' / '료', '41분 10' / '초') — lines break at spaces only
    th,
    td {
      padding: 8px 10px;
      // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
      border-bottom: 1px solid $st-border !important;
      text-align: left;
      vertical-align: top;
      word-break: keep-all;
      overflow-wrap: break-word;
    }

    thead th {
      background: $st-bg;
      font-size: 12px;
      font-weight: 600;
      color: $st-text-secondary;
      white-space: nowrap;
    }

    // Must beat the table's left default, so it is repeated inside the table
    .is-r,
    .cost-more__amount {
      text-align: right;
    }
  }

  &__sr {
    @include st-sr-only;
  }

  &__col-name {
    width: 17%;
  }

  &__col-usage {
    width: 21%;
  }

  &__col-unit {
    width: 11%;
  }

  &__col-amount {
    width: 13%;
  }

  // Section head (서티 청구 / 원가)
  &__section th {
    padding: 6px 10px;
    background: $st-bg;
    border-bottom: 1px solid $st-border;
  }

  &__section-title {
    font-size: 12px;
    font-weight: 700;
    color: $st-text-secondary;
  }

  &__section-sub {
    margin-left: 8px;
    font-size: 11px;
    font-weight: normal;
    color: $st-muted;
  }

  &__row {
    &.is-group {
      background: #fff;

      .cost-more__name-main {
        font-size: 13px;
        font-weight: 700;
      }

      .cost-more__value {
        font-weight: 700;
      }
    }

    &.is-level-1 {
      background: #fcfcfd;

      .cost-more__name-main {
        font-weight: 600;
        color: $st-text-secondary;
      }
    }

    // Child rows are indented and joined with └ — drawn in the indent, so it never wraps away from the name
    &.is-level-1 .cost-more__name {
      position: relative;
      padding-left: 30px;
    }

    &.is-level-1 .cost-more__name::before {
      content: '└';
      position: absolute;
      top: 8px;
      left: 14px;
      color: $st-border-strong;
    }
  }

  &__name {
    font-weight: normal;
  }

  &__name-line,
  &__value-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 6px;
  }

  &__value-line {
    justify-content: flex-end;

    &.is-start {
      justify-content: flex-start;
    }
  }

  &__name-main {
    color: $st-text;
  }

  &__name-sub,
  &__note {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    line-height: 1.45;
    color: $st-muted;
  }

  &__usage {
    color: $st-text-secondary;
  }

  // Basis row text (formula, segments, notes) — spans the value columns and wraps
  &__detail {
    font-variant-numeric: tabular-nums;
    line-height: 1.5;
    color: $st-text-secondary;
  }

  &__total-note {
    display: block;
    font-size: 11.5px;
    line-height: 1.5;
    color: $st-muted;
  }

  &__unit {
    @include st-num;
    color: $st-text-secondary;

    &.is-muted {
      color: $st-muted;
    }

    // Which billed item covers a cost row; the included line's unit — not amounts
    .is-muted {
      color: $st-muted;
    }
  }

  &__amount {
    text-align: right;

    .cost-more__note {
      text-align: right;
    }
  }

  &__value {
    @include st-num;
    font-weight: 600;
    color: $st-text;

    &.is-muted {
      color: $st-muted;
    }

    &.is-negative {
      color: $st-danger;
    }

    &.is-missing {
      color: $st-warning;
    }

    &.is-strong {
      font-size: 13.5px;
      font-weight: 700;
    }
  }

  &__missing {
    font-weight: 600;
    color: $st-warning;
  }

  // Gray reference — estimated costs and margins kept out of the totals
  &__ref {
    @include st-pill;
    padding: 0 6px;
    font-size: 10.5px;
    line-height: 16px;
  }

  &__src {
    @include st-pill;
    padding: 0 6px;
    font-size: 10.5px;
    line-height: 16px;

    &.is-override {
      @include st-pill-tone($st-override, $st-override-bg, $st-override-border);
    }
  }

  &__code {
    @include st-pill;
    padding: 0 6px;
    font-family: SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 10.5px;
    font-weight: 600;
    line-height: 16px;
    cursor: help;

    &.is-blocking {
      @include st-pill-tone($st-danger, $st-danger-bg, $st-danger-border);
    }
  }

  &__total {
    th,
    td {
      border-top: 2px solid $st-border-strong;
      background: #fff;
    }

    .cost-more__name-main {
      font-size: 13px;
      font-weight: 700;
    }

    .cost-more__value {
      font-size: 13.5px;
      font-weight: 700;
    }
  }

  &__vat {
    th,
    td {
      background: #fff;
    }

    .cost-more__name-main {
      font-weight: 600;
    }
  }

  tfoot tr:last-child {
    th,
    td {
      border-bottom: none !important;
    }
  }

  &__reference {
    margin: 0;
    font-size: 12px;
    color: $st-muted;
  }

  &__foot {
    margin: 0;
    font-size: 11.5px;
    line-height: 1.6;
    color: $st-muted;
  }
}
</style>
